import { ChangeDetectorRef, Directive, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { filter, first, map, publishLast, refCount, takeUntil } from 'rxjs/operators';
import { MemberService, PublicInfoResponse } from '@app/core/members/member.service';
import { getIdentifier, isEmail } from '@app/+login/deprecated-hybrid/helpers';
import { IdentifierType, MemberIdentifier } from '@app/core/_models/member-interface';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from '@app/core/settings/settings.service';
import { INVALID_EMAIL_ERROR_KEY, INVALID_IDENTIFIER_ERROR_KEY, INVALID_MSISDN_ERROR_KEY } from './consts';

// Also accepts spaces as some people tend to write phone number in their country's format
const isMsisdn = (input: string): boolean => /^\+?[\d ]+$/.test(input);

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class V2AuthAbstract implements OnInit, OnDestroy {
  @ViewChild('htmlInput') public input: ElementRef;

  public formControl = new UntypedFormControl('');
  public formErrors: { error?: string; [key: string]: unknown } = {};
  public submitted = false;
  public settingsObservable$ = this.settingsService.getSettingsOnce().pipe(publishLast(), refCount());

  private unsubscribe$ = new Subject();

  protected constructor(protected cdr: ChangeDetectorRef,
                        protected route: ActivatedRoute,
                        protected router: Router,
                        protected memberService: MemberService,
                        protected settingsService: SettingsService) {}

  public abstract verifyPublicInfo(identifier: MemberIdentifier, publicInfo: PublicInfoResponse): void;

  public ngOnInit(): void {
    const { state } = history;
    const { identifierValue } = state;

    if (identifierValue) { this.formControl.setValue(identifierValue); }

    this.route.queryParamMap
      .pipe(first(), map((queryParamMap) => queryParamMap.get('identifier')), filter((identifier) => !!identifier))
      .subscribe((identifier) => this.formControl.setValue(identifier));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public onSubmit(): void {
    this.settingsObservable$.subscribe(settings => {

      this.submitted = true;
      const value = this.formControl.value;

      if (settings.forced_identifier) {
        if (settings.forced_identifier === IdentifierType.MSISDN && !isMsisdn(value)) {
          this.formErrors = { error: INVALID_MSISDN_ERROR_KEY, identifier: value };

          return;
        }

        if (settings.forced_identifier === IdentifierType.EMAIL && !isEmail(value)) {
          this.formErrors = { error: INVALID_EMAIL_ERROR_KEY, identifier: value };
          return;
        }
      }

      isMsisdn(value) ? this.handleMsisdnSubmit(value) : this.handleEmailSubmit(value);
    });
  }

  protected handleEmailSubmit(value: any): void {
    const identifier = getIdentifier(value, IdentifierType.EMAIL);
    this.findUserAccount(identifier);
  }

  private handleMsisdnSubmit(value: any): void {
    const identifier = getIdentifier(value, IdentifierType.MSISDN);

    this.router.navigate(
      ['.', 'phone_confirmation'],
      { relativeTo: this.route, state: { identifier: identifier } },
    );
  }

  private findUserAccount(identifier: MemberIdentifier): void {
    this.memberService.isMember(identifier)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (publicInfo: PublicInfoResponse) => this.verifyPublicInfo(identifier, publicInfo),
        (error: HttpErrorResponse) => this.mapIdentifierErrors(identifier, error.error.error),
      );
  }

  private mapIdentifierErrors(identifier: MemberIdentifier, error: string): void {
    if (error.includes(INVALID_IDENTIFIER_ERROR_KEY)) {
      this.formErrors = { error: INVALID_IDENTIFIER_ERROR_KEY, identifier };
    }
    this.cdr.detectChanges();
  }
}
